import { formatDistance } from "date-fns";
import { fr } from "date-fns/locale";
import { ExternalLink, FileCog2, Signature, Trash2 } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

import type { ClaimWithRelations } from "../../api/use-claim";
import {
  useDeleteTravelAgencyContract,
  useGenerateTravelAgencyReceivable,
  useSendTravelAgencyContract,
} from "../../api/use-claim-actions";
import { openBoxFolder } from "../../lib/open-box";
import { CtaShell } from "./cta-shell";

const alertClasses = {
  success: "border-green-200 bg-green-50 text-green-800",
  info: "border-blue-200 bg-blue-50 text-blue-800",
  warning: "border-amber-200 bg-amber-50 text-amber-800",
};

function StatusAlert({
  type,
  title,
  message,
}: {
  type: keyof typeof alertClasses;
  title: string;
  message?: string;
}) {
  return (
    <div className={cn("rounded-md border p-4 text-sm", alertClasses[type])}>
      <p className="font-medium">{title}</p>
      {message ? <p className="mt-1">{message}</p> : null}
    </div>
  );
}

/** Port of states-cta/TravelAgencySignatureCTA.vue — cession agence → Upclaim. */
export function TravelAgencySignatureCTA({ claim }: { claim: ClaimWithRelations }) {
  const generateReceivable = useGenerateTravelAgencyReceivable(claim.id);
  const sendContract = useSendTravelAgencyContract(claim.id);
  const deleteContract = useDeleteTravelAgencyContract(claim.id);

  const signature = claim.signatures.find((s) => s.signatureType === "TRAVEL_AGENCY");
  const signatureDate = signature?.signedAt
    ? new Date(signature.signedAt)
    : signature?.statusUpdatedAt
      ? new Date(signature.statusUpdatedAt)
      : null;

  const [isAuto, setIsAuto] = useState(
    Boolean(
      claim.legalSpecification.impactedSegment.reservation.travelAgencyOffice.travelAgency?.isAutoSignature,
    ),
  );

  async function generateAgencyReceivableDocument() {
    const response = await generateReceivable.mutateAsync();
    if (response.boxFileId) {
      window.open(`https://app.box.com/file/${response.boxFileId}`, "_blank");
    }
  }

  return (
    <CtaShell legalStepName={claim.claimLegalStep.name}>
      <p className="mt-1 border-b pb-4 text-sm text-gray-500">
        Quelques étapes sont nécessaires pour démarrer le dossier.
      </p>

      <div className="mt-2 border-b py-4">
        <h3 className="text-sm font-medium text-gray-900">1. Vérifier les documents</h3>
        <p className="text-sm text-gray-500">
          Les cessions de créances (passager &amp; entreprise), éventuellement les factures.
        </p>
        <div className="my-2">
          <Button variant="outline" onClick={() => openBoxFolder(claim.boxFolderId)}>
            <ExternalLink />
            Ouvrir le dossier Box
          </Button>
        </div>
      </div>

      <div className="border-b py-4">
        <h3 className="text-sm font-medium text-gray-900">2. Générer la cession agence</h3>
        <p className="text-sm text-gray-500">
          Cela va générer le document de cession de l'agence envers Upclaim, au format .docx
        </p>
        <div className="my-2">
          <Button
            variant="outline"
            onClick={generateAgencyReceivableDocument}
            disabled={generateReceivable.isPending}
          >
            <FileCog2 />
            Générer la cession (.docx)
          </Button>
        </div>
      </div>

      {!signature ? (
        <div className="my-4">
          <h3 className="text-sm font-medium text-gray-900">3. Envoyer la cession agence</h3>
          <div className="text-sm text-gray-500">
            Cela va envoyer une demande de signature électronique à{" "}
            <code className="font-medium text-blue-600">{claim.tenantAssociation.sourceTenant.email}</code>
          </div>
          <Label className="my-4 inline-flex cursor-pointer items-center rounded-lg border p-4 shadow">
            <Checkbox
              checked={isAuto}
              onCheckedChange={(checked) => setIsAuto(checked === true)}
              className="mr-2"
            />
            <span className="text-sm/6">
              <span className="font-medium text-gray-900">Signature auto</span>{" "}
              <span className="text-gray-500">Signer à la place de l'agence.</span>
            </span>
          </Label>
          <div>
            <button
              type="button"
              onClick={() => sendContract.mutate({ isAuto })}
              disabled={sendContract.isPending}
              className="inline-flex cursor-pointer items-center gap-x-1.5 rounded bg-white px-2 py-1.5 text-sm font-semibold text-gray-700 shadow-sm ring-1 ring-gray-300 ring-inset hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Signature className="h-4 w-4 text-gray-400" aria-hidden="true" />
              {isAuto ? <span>Signer à la place de l'agence →</span> : <span>Envoyer la cession agence</span>}
            </button>
          </div>
        </div>
      ) : null}

      <div className="my-6 rounded-md border bg-white p-4">
        <p className="mb-4 leading-7 font-semibold">Statut de la signature Agence au profit d'Upclaim</p>
        {!signature ? (
          <StatusAlert type="warning" title="Aucune signature envoyée à l'agence" />
        ) : (
          <div>
            {signature.status === "COMPLETED" ? (
              <StatusAlert
                type="success"
                title="Signature à l'agence effectuée"
                message={formatDistance(signatureDate ?? new Date(), new Date(), {
                  addSuffix: true,
                  locale: fr,
                })}
              />
            ) : signature.status === "PENDING" ? (
              <StatusAlert
                type="info"
                title="Signature en attente..."
                message={formatDistance(new Date(signature.createdAt ?? Date.now()), new Date(), {
                  addSuffix: true,
                  locale: fr,
                })}
              />
            ) : signature.status ? (
              <StatusAlert
                type="warning"
                title={`Attention : statut signature = ${signature.status}`}
                message={formatDistance(new Date(signature.createdAt ?? Date.now()), new Date(), {
                  addSuffix: true,
                  locale: fr,
                })}
              />
            ) : null}
            <div className="mt-4">
              <Button
                variant="destructive"
                onClick={() => deleteContract.mutate()}
                disabled={deleteContract.isPending}
              >
                <Trash2 />
                Supprimer la signature Agence ({signature.service})
              </Button>
            </div>
          </div>
        )}
      </div>
    </CtaShell>
  );
}
